import * as React from 'react';
import { useCallback, useState } from 'react';

/** Material UI Packages. */
import { Box, Button, Dialog, Paper, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';

/** Redux packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Redux Reducers. */
import { addRoute } from 'shared/store/slices/routeSlice';
import { deleteNewRoute } from 'shared/store/slices/newRouteSlice';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import { PaperComponent } from 'shared/ui/DraggableDialog';
import DialogHeader from 'shared/ui/DialogHeader';
import { createSocketData } from 'shared/lib/fetchSocket';

/** Config for headers. */
const HEADERS_CONFIG = {
    ocean: [
        { label: 'POL', gridSize: 4 },
        { label: 'POD', gridSize: 4 },
        { label: 'LEAD TIME', gridSize: 2 },
        { label: 'OPUS REF#', gridSize: 2 },
    ],
    air: [
        { label: 'AOL', gridSize: 4 },
        { label: 'AOD', gridSize: 4 },
        { label: 'LEAD TIME', gridSize: 2 },
        { label: 'OPUS REF#', gridSize: 2 },
    ],
    rail: [
        { label: 'POD', gridSize: 4 },
        { label: 'F.DEST', gridSize: 4 },
        { label: 'LEAD TIME', gridSize: 2 },
        { label: 'OPUS REF#', gridSize: 2 },
    ],
};

/** Pick the header labels for the tab currently shown (1: ocean, 2: air, 3: rail). */
const headersFor = (tabValue) => {
    if (tabValue === 3) return HEADERS_CONFIG.rail;
    return tabValue === 1 ? HEADERS_CONFIG.ocean : HEADERS_CONFIG.air;
};

/** Config for fields. */
const FIELD_CONFIG = {
    pol: [
        { label: 'Code', field: 'pol_code', size: 2.5 },
        { label: 'Name', field: 'pol_name', size: 3.5 },
        { label: 'Country', field: 'pol_country', size: 4 },
    ],
    pod: [
        { label: 'Code', field: 'pod_code', size: 2.5 },
        { label: 'Name', field: 'pod_name', size: 3.5 },
        { label: 'Country', field: 'pod_country', size: 4 },
    ],
};

export default function NewUpdatesDialog(props) {
    /** Get props from TransportRoute.js. */
    const { open, setOpen, tabValue } = props;

    /** Load dispatch to update routes. */
    const dispatch = useDispatch();

    /** State for saving the new updated routes. */
    const newRouteList = useSelector((state) => state.newRouteList);

    /** Lead time typed per pending route, keyed by new route id. */
    const [leadtimes, setLeadtimes] = useState({});

    const leadtimeOf = (route) => leadtimes[route.id] ?? String(route.leadtime ?? 1);

    /** Handler to apply the updated route to the data grid. */
    const applyNewRoute = useCallback(
        (route) => {
            const leadtime = Number(leadtimeOf(route));
            if (!Number.isInteger(leadtime) || leadtime <= 0) {
                dispatch(
                    setSnackbar({
                        open: true,
                        message: 'Please check your days. Lead time must be a positive integer.',
                        severity: 'warning',
                    })
                );
                return;
            }

            if (window.confirm('Do you want to add the new route?')) {
                const routeToSend = {
                    ...route,
                    leadtime,
                    // 필드명 통일 (opus_reference_id vs il_opus_reference_id)
                    il_opus_reference_id: route.il_opus_reference_id || route.opus_reference_id,
                };

                createSocketData(
                    dispatch,
                    tabValue,
                    'row',
                    'il_route',
                    null,
                    routeToSend,
                    (response) => {
                        dispatch(addRoute(response.data)); // add new update route to exist route list.
                        dispatch(deleteNewRoute(route.id)); // remove the state from new update route list.
                        dispatch(
                            setSnackbar({
                                open: true,
                                message: 'Successfully added the new route.',
                                severity: 'success',
                            })
                        );
                    },
                    (response) => {
                        if (response.status === 201) {
                            dispatch(deleteNewRoute(route.id)); // remove the state from new update route list.
                        }
                    }
                );
            }
        },
        [dispatch, tabValue, leadtimes]
    );

    /** Render route section. */
    const renderRouteSection = (sectionData, sectionKey) => {
        return (
            <Grid size={4} sx={{ borderRight: 'solid 1px #e2e2e2', px: 1.5, py: '19px' }}>
                {sectionData.map((item) => {
                    return (
                        <Box
                            key={item.label}
                            sx={{
                                display: 'inline-flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                width: `${item.size * 10}%`,
                            }}
                        >
                            <Typography sx={{ color: '#00647e', fontSize: 13, fontWeight: 600, mr: 0.4 }}>
                                {item.label}
                            </Typography>
                            <Typography sx={{ fontSize: 13 }}>{item.value}</Typography>
                        </Box>
                    );
                })}
            </Grid>
        );
    };

    /** Render route item. */
    const renderRouteItem = (route) => (
        <Paper
            key={route.id}
            sx={{
                width: '100%',
                fontSize: 13,
                mb: '10px',
                boxShadow: '0 0 6px 0 rgba(0, 0, 0, 0.16)',
                borderRadius: '10px',
            }}
        >
            <Grid container rowSpacing={2}>
                {renderRouteSection(
                    FIELD_CONFIG.pol.map((item) => ({
                        ...item,
                        value: route[item.field],
                    })),
                    'pol'
                )}
                {renderRouteSection(
                    FIELD_CONFIG.pod.map((item) => ({
                        ...item,
                        value: route[item.field],
                    })),
                    'pod'
                )}

                <Grid
                    size={2}
                    sx={{
                        borderRight: 'solid 1px #e2e2e2',
                        px: 1.5,
                        py: '13px',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <TextField
                        size="small"
                        type="number"
                        value={leadtimeOf(route)}
                        onChange={(event) =>
                            setLeadtimes((prev) => ({ ...prev, [route.id]: event.target.value }))
                        }
                        inputProps={{ min: 1, step: 1, 'aria-label': 'Lead time in days' }}
                        sx={{ width: '90px', '& input': { fontSize: 13, py: '6px' } }}
                    />
                    <Typography sx={{ fontSize: 13, ml: 0.8, color: '#00647e' }}>days</Typography>
                </Grid>

                <Grid size={1} sx={{ borderRight: 'solid 1px #e2e2e2', px: '10px', py: '19px' }}>
                    <Typography sx={{ fontSize: 13 }}>{route.opus_reference_id}</Typography>
                </Grid>

                <Grid size={1} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Button variant="contained" size="small" onClick={() => applyNewRoute(route)}>
                        Add
                    </Button>
                </Grid>
            </Grid>
        </Paper>
    );

    /** Render empty state. */
    const renderEmptyState = () => (
        <Paper
            sx={{
                width: '100%',
                height: '50px',
                boxShadow: '0 0 6px 0 rgba(0, 0, 0, 0.16)',
                borderRadius: '10px',
            }}
        >
            <Typography
                sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'gray',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                }}
            >
                No rows
            </Typography>
        </Paper>
    );

    /** Render route list. */
    const renderRouteList = () => (
        <Box>{newRouteList.length > 0 ? newRouteList.map(renderRouteItem) : renderEmptyState()}</Box>
    );

    return (
        <Dialog
            onClose={() => setOpen(false)}
            open={open}
            maxWidth={'1544px'}
            PaperProps={{ sx: { borderRadius: '18px' } }}
            PaperComponent={PaperComponent}
        >
            <DialogHeader cursor title="NEW UPDATES" onClose={() => setOpen(false)} sx={{ width: '80vw' }} />
            <Box sx={{ p: 2, maxHeight: '50vh' }}>
                <Grid container sx={{ mb: 2 }}>
                    {headersFor(tabValue).map((item, index) => (
                        <Grid size={item.gridSize} key={index} sx={{ px: '5px', display: 'flex' }}>
                            <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#003a52' }}>
                                {item.label}
                            </Typography>
                        </Grid>
                    ))}
                </Grid>

                {renderRouteList()}
            </Box>
        </Dialog>
    );
}
